import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RefreshButton from '../../../components/fields/buttons/refresh-button';
import TableTab from "../../../components/tabs/table-tab";
import { FILTER_DATE } from "../../../constants/filter-types";
import { REPORTS_TRANSACTION_VINGCARD } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import VingcardTransactionColumn from "../../../models/ColumnDefinitions/VingcardTransactionColumn";
import { Filter } from "../../../models/Filter";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import VingcardTransactionSearch from "../../../models/search/VingcardTransactionSearch";
import { auditRunReport } from "../../../services/auditService";
import { getVingcardTransactionReport } from "../../../services/reportService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getTodayDate } from "../../../utilities/DateUtils";

interface RptProps {
    reportId: string;
}

const VingcardTransactionReport: FC<RptProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const [refreshReport, setRefreshReport] = useState(true);

    useEffect(() => {
        if (refreshReport) {
            dataRefresh(getDataMethod)
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_TRANSACTION_VINGCARD;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        // Filters that should always show for this view
        const staticFilterKeys = ["dateOnly"];

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then((response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter = [{
                    isCustomFilter: true,
                    key: "dateOnly",
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(true),
                        value: getTodayDate(false)
                    }]
                }];
            }
        }).catch((error) => {
            console.log("Failed to load user preferences")
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        })
    }
    // <-- end context

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getVingcardTransactionReport(listParameters);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    return (<TableContextProvider
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={VingcardTransactionColumn}
        searchDescriptor={VingcardTransactionSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.vingcardTransactions)}
            selectRows={false}
            showFilterMenu={true}
            cyName="vingcardTransactionReports">
            <RefreshButton onRefresh={setRefreshReport} />
        </TableTab>
    </TableContextProvider >
    );
};

export default VingcardTransactionReport;
